// deployment_class: internal_only
/**
 * App — top-level router.
 *
 * Plan 14 §Task 2 + Spec 12 §4. Single SPA, route-level segregation. Each
 * portal sub-tree is lazy-loaded (OQ-Sh3 — lazy wins ~30-40% bundle size
 * for users who don't visit every portal). Auth wrappers gate the admin
 * portals via OAuth scope.
 *
 * CLOSED-SYSTEM GATE (2026-06-20): Kathedral.ai is gated behind authentication
 * for the demo. A logged-out visitor sees the LOGIN page and nothing else — the
 * `/auth/*` routes are rendered OUTSIDE the top-level `RequireAuthGate` (so an
 * anonymous user can sign in, create an account, and CRITICALLY click the
 * email-verification link → land on `/auth/login?verified=1`); EVERY other
 * route is wrapped by the gate, which redirects unauthenticated visitors to
 * `/auth/login`. The Marketplace is NO LONGER a public landing.
 *
 * Routes:
 *   /                                redirects to /auth/login (default landing for anon)
 *   /auth/login                      Login / create-account form (PUBLIC · ungated)
 *   /auth/logout                     Logout flow (PUBLIC · ungated)
 *   ── everything below is wrapped by RequireAuthGate (auth required) ──
 *   /marketplace/*                   gated (was public browse pre-2026-06-20)
 *   /partnerships/*                  RequireAuth (RequireOrgMembership inside the sub-tree)
 *   /admin/*                         INTERNAL BUILD ONLY — delegated to App.internal
 *                                    (Sales · Data-Analytics · Internal-Ops incl. Novello).
 *                                    Absent from the customer build (DCE'd). See
 *                                    docs/superpowers/research/2026-07-01-internal-build-target-separation.md
 *   *                                NotFound (404)
 *
 * Persistent TopBar (Task 6) renders above the routed view; AskKathedral
 * overlay (Task 9) renders alongside.
 */
import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { RequireAuth } from './shell/auth/RequireAuth';
import { DashboardHome } from './shell/DashboardHome';
import { LandingRedirect } from './shell/role-view/LandingRedirect';
import { RequireAuthGate } from './shell/auth/RequireAuthGate';
import { Login } from './shell/auth/Login';
import { Logout } from './shell/auth/Logout';
import { LandingPreview } from './shell/LandingPreview';
import { NotFound } from './shell/error/NotFound';
import { TopBar } from './shell/components/TopBar';
import { AskKathedralOverlay } from './shell/components/AskKathedralOverlay';
import { SkipLink } from './shell/components/SkipLink';
import { useRouteFocus } from './shell/hooks/useRouteFocus';
import { useApplyPreferences } from './shell/hooks/useApplyPreferences';
import { SurfaceHub } from './shell/csa-surface/SurfaceHub';
import { registerPersonalSurfaces } from './shell/account/personal-surfaces';
import { registerAccountAdminSurfaces } from './shell/account/account-admin-surfaces';

// Enroll the customer-facing surfaces into the CSA surface registry once, at
// module load. Routes/nav under /account/* are a projection of these.
registerPersonalSurfaces(); // Tier 1 — /account/*
registerAccountAdminSurfaces(); // Tier 2 — /account/organization/*

const MarketplacePortal = lazy(() => import('./marketplace/MarketplacePortal'));
const PartnershipPortal = lazy(() => import('./partnership/PartnershipPortal'));
const ProjectsAdmin = lazy(() =>
  import('./projects/edit/ProjectsAdmin').then((m) => ({ default: m.ProjectsAdmin })),
);
const NewProject = lazy(() =>
  import('./projects/create/NewProject').then((m) => ({ default: m.NewProject })),
);
const OpenProject = lazy(() =>
  import('./projects/create/OpenProject').then((m) => ({ default: m.OpenProject })),
);
const ProjectHome = lazy(() =>
  import('./projects/create/ProjectHome').then((m) => ({ default: m.ProjectHome })),
);
const BoardPortal = lazy(() =>
  import('./board/BoardPortal').then((m) => ({ default: m.BoardPortal })),
);
const RunboardPage = lazy(() =>
  import('./projects/runboard/Runboard').then((m) => ({ default: m.RunboardPage })),
);

// INTERNAL BUILD SEPARATION (2026-07-01) — the internal_only route subtree
// (Sales · Data-Analytics · Internal-Ops, the last being the sole importer of the
// Novello UI build/UAT harness) lives in App.internal.tsx. It is INCLUDED by default —
// dev, tests, and the internal artifact all carry it (devs use Novello; the matrix tests
// exercise the internal portals). The CUSTOMER build is the opt-OUT: it sets
// VITE_CUSTOMER_BUILD=1, so INCLUDE_INTERNAL folds to a literal `false` and Rollup
// dead-code-eliminates `import('./App.internal')` — Novello + the internal portals are
// PHYSICALLY ABSENT from the customer artifact, not merely gated (N40 · N58). Design:
// docs/superpowers/research/2026-07-01-internal-build-target-separation.md
const INCLUDE_INTERNAL = import.meta.env.VITE_CUSTOMER_BUILD !== '1';
const InternalRoutes = INCLUDE_INTERNAL ? lazy(() => import('./App.internal')) : null;

function PortalSuspenseFallback(): JSX.Element {
  return (
    <div
      className="min-h-screen bg-paper p-page text-warm-gray-d text-body-s"
      aria-busy="true"
    >
      Loading…
    </div>
  );
}

/**
 * Applies the authenticated user's saved VISUAL preferences (font scale / density
 * / reduced motion) to the document. Mounted INSIDE the auth gate so the
 * preferences fetch only fires for a signed-in user. Renders nothing.
 */
function ApplyPreferences(): null {
  useApplyPreferences();
  return null;
}

export function App(): JSX.Element {
  // Route-change focus management — moves focus to the new route's <h1>
  // (or <main> fallback) on every pathname change so screen readers
  // announce the new page. Design System §9.4.
  useRouteFocus();
  return (
    <div className="min-h-screen flex flex-col bg-canvas text-ink">
      {/* SkipLink MUST be the first focusable element. Design System §9.3. */}
      <SkipLink />
      {/* Persistent app chrome — sign-out, portal nav, org/profile. The TopBar
       * renders on EVERY authenticated route, including the home (the
       * dashboard); it self-hides only on /auth/login + /auth/logout. The home
       * must always carry the app controls — a logged-in user needs sign-out. */}
      <TopBar />
      {/*
       * App-level wrapper — NOT a <main> landmark. Each portal layout
       * provides its own single <main id="main-content"> so the page has
       * exactly one main landmark (Design System §9.10). Login and 404
       * routes (which don't use a portal layout) wrap their content in
       * <main> directly.
       */}
      <div id="main-content" tabIndex={-1} className="flex-1 outline-none">
        <Suspense fallback={<PortalSuspenseFallback />}>
          <Routes>
            {/*
             * PUBLIC (ungated) auth routes. These MUST stay reachable to an
             * ANONYMOUS visitor — sign-in, create-account, and the
             * email-verification landing (`/auth/login?verified=1`). They are
             * rendered OUTSIDE RequireAuthGate so a logged-out user is never
             * redirected away from them (no redirect loop).
             */}
            <Route path="/auth/login" element={<Login />} />
            <Route path="/auth/logout" element={<Logout />} />
            {/* PUBLIC marketing landing (kathedral.ai). Ungated — the only public app
                surface besides /auth/*. All content is illustrative + fictional. */}
            <Route path="/landing" element={<LandingPreview />} />

            {/*
             * CLOSED-SYSTEM GATE. Every non-auth route is wrapped: an
             * unauthenticated visitor is redirected to /auth/login and sees the
             * Marketplace/portals only AFTER authenticating. The default
             * landing `/` redirects to /auth/login for anonymous users (and the
             * gate immediately admits authenticated users so the inner
             * /marketplace redirect still applies to them).
             */}
            <Route
              path="*"
              element={
                <RequireAuthGate>
                  {/* Apply the signed-in user's visual preferences app-wide. */}
                  <ApplyPreferences />
                  <Routes>
                    {/* Home = Layer-B landing (Role-View §2). CEO-tier + staff/
                     * advisory render the dashboard IN PLACE (byte-identical to
                     * before); other roles redirect to their home. */}
                    <Route path="/" element={<LandingRedirect />} />

                    {/* The Executive cockpit as an explicitly reachable route so
                        internal admins (who land on internal-ops) can view the
                        customer dashboards/modules from the portal switcher
                        (E3 2026-07-06). CEO-tier still reach the cockpit via '/'. */}
                    <Route
                      path="/dashboard/*"
                      element={
                        <RequireAuth>
                          <DashboardHome />
                        </RequireAuth>
                      }
                    />

                    {/* Marketplace — GATED (was public browse pre-2026-06-20).
                        NOTE (bug_UI_1a): the switcher ENTRY is internal-admin-only,
                        but the ROUTE stays open to its real users (holders /
                        account_admin / marketplace_operations) — locking the route
                        to internalops.read would break the Verified Marketplace
                        (Plan 11A/11B). Pending E3's product call. */}
                    <Route path="/marketplace/*" element={<MarketplacePortal />} />

                    {/* Account umbrella (Settings & Account IA spec 2026-06-24)
                        — Tier 1 Personal, zone settings.personal. Routes/nav are
                        a PROJECTION of the CSA surface registry, not hand-wired.
                        Authed (inside the gate); per-surface scope-gating inside. */}
                    {/* Tier 2 — Account / Organization admin (zone settings.account).
                        More specific than /account/* so it wins for these paths. */}
                    <Route
                      path="/account/organization/*"
                      element={
                        <SurfaceHub
                          zone="settings.account"
                          basePath="/account/organization"
                          title="Organization"
                        />
                      }
                    />
                    <Route
                      path="/account/*"
                      element={
                        <SurfaceHub zone="settings.personal" basePath="/account" title="Account" />
                      }
                    />
                    {/* Legacy redirects — no dead links (one-time declarative map). */}
                    <Route path="/profile" element={<Navigate to="/account/profile" replace />} />
                    <Route path="/settings" element={<Navigate to="/account/preferences" replace />} />

                    {/* Partnership Portal — auth required; org-membership enforced inside */}
                    <Route
                      path="/partnerships/*"
                      element={
                        <RequireAuth>
                          <PartnershipPortal />
                        </RequireAuth>
                      }
                    />

                    {/* Board Portal — the PHA board / commissioner seat (Board
                     * Projection spec §3). Customer-side, so it ships in the
                     * customer build (unlike Sales/Internal-Ops). Scope gating is
                     * SERVER-side on /v3/board/projection; this route only decides
                     * what renders. Its absence is what sent every board member to
                     * NotFound — see landing-reachability.test.ts, which now fails
                     * if any landing target stops resolving. */}
                    <Route
                      path="/board"
                      element={
                        <RequireAuth>
                          <BoardPortal />
                        </RequireAuth>
                      }
                    />

                    {/* Open a project — an ENUMERATED list. The user picks from
                     * what exists; they never type a name to find one. */}
                    <Route
                      path="/projects"
                      element={
                        <RequireAuth>
                          <OpenProject />
                        </RequireAuth>
                      }
                    />

                    {/* A project, opened — where creating one LANDS and where the
                     * open-project list leads. Declared before /projects/new so
                     * the literal segment is not swallowed by :id. */}
                    <Route
                      path="/projects/:id"
                      element={
                        <RequireAuth>
                          <ProjectHome />
                        </RequireAuth>
                      }
                    />

                    {/* New project — name it, then choose everything it carries.
                     * Reached from the Project menu in the top bar; the server
                     * gates the create itself on `create:projects`. */}
                    <Route
                      path="/projects/new"
                      element={
                        <RequireAuth>
                          <NewProject />
                        </RequireAuth>
                      }
                    />

                    {/* Projects module — Wave 2 functional-minimal edit harness (auth required) */}
                    <Route
                      path="/projects/manage"
                      element={
                        <RequireAuth>
                          <ProjectsAdmin />
                        </RequireAuth>
                      }
                    />

                    {/* Projects module — Wave 3 Runboard: the polished surface over one
                     * workflow instance (auth required; org scoping is server-side). */}
                    <Route
                      path="/projects/runboard/:instanceId"
                      element={
                        <RequireAuth>
                          <RunboardPage />
                        </RequireAuth>
                      }
                    />

                    {/* INTERNAL route subtree (/admin/sales · /admin/data-analytics ·
                     * /admin/internal-ops). Present ONLY in the internal build — in the
                     * customer build InternalRoutes is null (DCE'd) so these paths fall
                     * through to NotFound below. Scope gating lives inside App.internal. */}
                    {InternalRoutes && <Route path="/admin/*" element={<InternalRoutes />} />}

                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </RequireAuthGate>
              }
            />
          </Routes>
        </Suspense>
      </div>
      <AskKathedralOverlay />
    </div>
  );
}
